import { Trans, useLingui } from "@lingui/react/macro";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { PRIVACY_POLICY_REVISION } from "../lib/privacyPolicyRevision";
import { SELLER } from "../lib/seller";
import { useSellerName } from "../lib/useSellerName";
import {
  Clause,
  ClauseList,
  LegalDocument,
  LegalSection,
  SellerCard,
  ShortSummary,
} from "./LegalDocument";

const linkClassName = "text-accent hover:text-accent-strong";

type PurposeProps = {
  purpose: ReactNode;
  data: ReactNode;
  basis: ReactNode;
  retention: ReactNode;
};

// Карточка цели обработки: что за цель, какие данные, основание и срок хранения.
const Purpose = ({ purpose, data, basis, retention }: PurposeProps) => (
  <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-3.5 gap-y-1 rounded-[10px] border border-border bg-surface px-3.5 py-3">
    <dt className="text-subtle">
      <Trans>Purpose</Trans>
    </dt>
    <dd className="font-semibold text-foreground">{purpose}</dd>
    <dt className="text-subtle">
      <Trans>Data</Trans>
    </dt>
    <dd>{data}</dd>
    <dt className="text-subtle">
      <Trans>Basis</Trans>
    </dt>
    <dd>{basis}</dd>
    <dt className="text-subtle">
      <Trans>Retention</Trans>
    </dt>
    <dd>{retention}</dd>
  </dl>
);

// Политика конфиденциальности (`/privacy`) по макету design/prototype/Privacy.dc.html. Steam ID
// и данные, привязанные к нему, Relock не считает персональными данными по 152-ФЗ: личность
// игрока сервис не знает. Для пользователей из ЕС — отдельный раздел по GDPR. Текст описывает
// фактическое поведение продукта — см. rules.md фичи.
export const PrivacyPolicy = () => {
  const { t } = useLingui();
  const seller = useSellerName();
  const inn = SELLER.inn;
  const ogrnip = SELLER.ogrnip;
  const email = (
    <a href={`mailto:${SELLER.email}`} className={linkClassName}>
      {SELLER.email}
    </a>
  );

  return (
    <LegalDocument title={t`Privacy policy`} revision={PRIVACY_POLICY_REVISION}>
      <ShortSummary>
        <li>
          <Trans>
            You sign in with Steam, and we receive your Steam ID, nickname and avatar. We never see
            your Steam password.
          </Trans>
        </li>
        <li>
          <Trans>
            We don't know who you are: we don't ask for your name, email, phone number or card
            details. Payments are handled by the payment service.
          </Trans>
        </li>
        <li>
          <Trans>
            We need the data to analyze your matches. We don't sell it, and there are no ads.
          </Trans>
        </li>
        <li>
          <Trans>
            You can delete your account and data in the player menu, instantly and for good.
          </Trans>
        </li>
      </ShortSummary>

      <LegalSection title={<Trans>1. General provisions</Trans>}>
        <Clause n="1.1">
          <Trans>
            This Policy describes what data about users of the Relock service is processed, for what
            purpose, how long it is kept and how a user can obtain, correct or delete it.
          </Trans>
        </Clause>
        <Clause n="1.2">
          <Trans>
            The Service is provided by {seller}, TIN {inn}, OGRNIP {ogrnip} (the “Provider”).
          </Trans>
        </Clause>
        <Clause n="1.3">
          <Trans>
            The Policy applies to all data the Provider receives about a User when they use the
            Service and is publicly available at relock.gg/privacy.
          </Trans>
        </Clause>
        <Clause n="1.4">
          <Trans>
            The terms of use of the Service and the Pro subscription are set out in the{" "}
            <Link to="/terms" className={linkClassName}>
              User agreement and public offer
            </Link>
            .
          </Trans>
        </Clause>
      </LegalSection>

      <LegalSection title={<Trans>2. Terms</Trans>}>
        <Clause n="2.1">
          <Trans>
            <b>Service</b> means the Relock web service at relock.gg: Deadlock match analyses,
            drills and statistics.
          </Trans>
        </Clause>
        <Clause n="2.2">
          <Trans>
            <b>User</b> means an individual who has signed in to the Service with a Steam account.
          </Trans>
        </Clause>
        <Clause n="2.3">
          <Trans>
            <b>Steam ID</b> means the numeric identifier of a Steam account. It contains no name,
            contact details or other information about its owner.
          </Trans>
        </Clause>
        <Clause n="2.4">
          <Trans>
            <b>Analysis</b> means the result of an automated analysis of a User's match: the
            mistakes found and the comparison with strong players.
          </Trans>
        </Clause>
      </LegalSection>

      <LegalSection title={<Trans>3. What data is processed</Trans>}>
        <Clause n="3.1">
          <Trans>
            Steam account data: Steam ID, display name and avatar link. Steam provides them at
            sign-in via Steam OpenID and updates them at every sign-in. The Provider never receives
            the Steam password.
          </Trans>
        </Clause>
        <Clause n="3.2">
          <Trans>
            Game data: match history, match details and rank from the public statistics service
            deadlock-api, as well as analyses, mistakes found, match ratings, the play style summary
            and when each analysis was first opened.
          </Trans>
        </Clause>
        <Clause n="3.3">
          <Trans>
            Friends: the Steam friend list (their Steam IDs) from the Steam Web API. For friends who
            play Deadlock: name, avatar, number of matches in the last 30 days and matches played
            together. Friends who also use the Service see the User in their friends table; the User
            can hide themselves on the Friends page.
          </Trans>
        </Clause>
        <Clause n="3.4">
          <Trans>Invitations: the User's invite code and who invited them.</Trans>
        </Clause>
        <Clause n="3.5">
          <Trans>Settings: interface language and personal settings.</Trans>
        </Clause>
        <Clause n="3.6">
          <Trans>Technical data:</Trans>
        </Clause>
        <ClauseList>
          <li>
            <Trans>
              sign-in session, status of analysis requests and the number of refusals due to the
              daily limit;
            </Trans>
          </li>
          <li>
            <Trans>
              server logs: time, page address, browser and IP address without its last part, which
              cannot identify a device;
            </Trans>
          </li>
          <li>
            <Trans>
              activity days: date, time of the first visit that day and device type (phone or
              computer);
            </Trans>
          </li>
          <li>
            <Trans>
              first-visit source: site domain, utm_source, utm_medium and utm_campaign tags, and the
              app name if the site was opened inside an app.
            </Trans>
          </li>
        </ClauseList>
        <Clause n="3.7">
          <Trans>
            Visit statistics (section 6): page address without parameters, referring site, browser,
            operating system, device type, country, region and city from the IP address, and names
            of actions on pages. The IP address itself is not stored.
          </Trans>
        </Clause>
        <Clause n="3.8">
          <Trans>
            Payments, if the User pays for a Pro subscription: payment number, amount, date, status
            and method (SBP or card), and for autopay the subscription number and its status. The
            User enters card or account details and the address for the receipt on the payment
            service's page; they are not passed to the Provider.
          </Trans>
        </Clause>
        <Clause n="3.9">
          <Trans>
            Data of other players in a match: Steam IDs, nicknames and game statistics from the
            public deadlock-api, shown in the analysis.
          </Trans>
        </Clause>
        <Clause n="3.10">
          <Trans>
            The Service does not ask for or store names, email addresses, phone numbers, bank card
            details, precise location or any other information that could identify the User. All
            data in this section is linked to the Steam ID; the Provider does not match it to the
            identity of its owner.
          </Trans>
        </Clause>
      </LegalSection>

      <LegalSection title={<Trans>4. Purposes and retention</Trans>}>
        <Clause n="4.1">
          <Trans>The Provider processes data only for the following purposes:</Trans>
        </Clause>
        <Purpose
          purpose={
            <Trans>Sign-in, analyses, drills, comparison with friends and invitations</Trans>
          }
          data={<Trans>clauses 3.1–3.5, 3.9</Trans>}
          basis={
            <Trans>
              performance of the User agreement; without data on friends and other players of a
              match, analyses and comparisons are inaccurate
            </Trans>
          }
          retention={<Trans>as long as the account exists</Trans>}
        />
        <Purpose
          purpose={<Trans>Accepting payment for Pro and tax accounting</Trans>}
          data={<Trans>clause 3.8</Trans>}
          basis={<Trans>performance of the contract and tax law requirements</Trans>}
          retention={<Trans>5 years after the payment</Trans>}
        />
        <Purpose
          purpose={<Trans>Reliable operation of the Service and protection against abuse</Trans>}
          data={<Trans>clause 3.6: session, server logs</Trans>}
          basis={<Trans>reliable operation of the Service</Trans>}
          retention={
            <Trans>
              session: until sign-out, at most two weeks; logs are overwritten as they fill up
            </Trans>
          }
        />
        <Purpose
          purpose={<Trans>Usage statistics and improving the Service</Trans>}
          data={<Trans>clause 3.6: activity days and visit source; clause 3.7</Trans>}
          basis={<Trans>improving the Service</Trans>}
          retention={
            <Trans>
              activity days and visit source: as long as the account exists; visit statistics are
              anonymized and kept to compare periods
            </Trans>
          }
        />
        <Clause n="4.2">
          <Trans>
            The Provider does not sell data, does not pass it on for advertising and does not make
            decisions with legal consequences for the User based on analyses.
          </Trans>
        </Clause>
      </LegalSection>

      <LegalSection title={<Trans>5. How data is processed</Trans>}>
        <Clause n="5.1">
          <Trans>
            Data is processed automatically. Analyses are produced by a program from match
            statistics.
          </Trans>
        </Clause>
        <Clause n="5.2">
          <Trans>Data and backups are stored on servers in the European Union.</Trans>
        </Clause>
        <Clause n="5.3">
          <Trans>
            On the Provider's instructions, data is processed by contractors for hosting, backups
            and error tracking. They act only on the Provider's instructions. Error reports contain
            no user data.
          </Trans>
        </Clause>
        <Clause n="5.4">
          <Trans>
            The Provider passes data to independent services, each with its own privacy policy:
          </Trans>
        </Clause>
        <ClauseList>
          <li>
            <Trans>
              Valve Corporation (Steam, USA): sign-in with Steam and requesting the friend list by
              Steam ID;
            </Trans>
          </li>
          <li>
            <Trans>
              deadlock-api: the Steam IDs of the User and their friends, to load matches and
              profiles;
            </Trans>
          </li>
          <li>
            <Trans>
              the payment service Platega (Russia): amount, order number and account number in the
              Service, if the User pays for Pro.
            </Trans>
          </li>
        </ClauseList>
        <Clause n="5.5">
          <Trans>Data is passed to other third parties only where the law requires it.</Trans>
        </Clause>
      </LegalSection>

      <LegalSection title={<Trans>6. Cookies and visit statistics</Trans>}>
        <Clause n="6.1">
          <Trans>
            The Service uses only strictly necessary cookies:{" "}
            <code className="font-mono text-foreground">sessionid</code> keeps the User signed in
            (removed at sign-out, expires after two weeks), and{" "}
            <code className="font-mono text-foreground">csrftoken</code> protects forms from
            requests sent by other sites. There are no advertising or third-party cookies.
          </Trans>
        </Clause>
        <Clause n="6.2">
          <Trans>
            Visit statistics are collected by Umami on the Provider's own server, without cookies. A
            visitor is counted by a code made from the IP address, the browser and a secret that
            changes every month, so visits cannot be linked to the User or across months. Nothing is
            collected if the browser sends “Do Not Track”.
          </Trans>
        </Clause>
      </LegalSection>

      <LegalSection title={<Trans>7. Retention, deletion and protection</Trans>}>
        <Clause n="7.1">
          <Trans>
            Data is kept no longer than its purposes require (section 4). Backups are kept for 30
            days.
          </Trans>
        </Clause>
        <Clause n="7.2">
          <Trans>
            The User can delete their account themselves: player menu at the bottom of the side menu
            (on a phone, open the side menu with the ☰ button first) → “Delete account” → “Delete
            forever”. The account, profile, settings, analyses, ratings and summary, loaded match
            history, friend list, invitations, activity days and visit source are deleted
            immediately. The data disappears from backups within 30 days.
          </Trans>
        </Clause>
        <Clause n="7.3">
          <Trans>
            When the account is deleted, autopay is turned off and payment records are kept for 5
            years without being linked to the Steam ID.
          </Trans>
        </Clause>
        <Clause n="7.4">
          <Trans>
            The data of the matches themselves is not deleted: it is shared by all 12 players of a
            match and public in deadlock-api.
          </Trans>
        </Clause>
        <Clause n="7.5">
          <Trans>
            The Provider protects the data: the connection to the Service is encrypted (HTTPS), only
            the Provider has access to the servers and backups, and no user passwords are stored
            because sign-in goes through Steam.
          </Trans>
        </Clause>
      </LegalSection>

      <LegalSection title={<Trans>8. User rights</Trans>}>
        <Clause n="8.1">
          <Trans>The User may:</Trans>
        </Clause>
        <ClauseList>
          <li>
            <Trans>find out what data about them is stored and get a copy of it;</Trans>
          </li>
          <li>
            <Trans>
              correct the data: the name and avatar are changed in Steam and update at the next
              sign-in;
            </Trans>
          </li>
          <li>
            <Trans>delete the account and data (clause 7.2);</Trans>
          </li>
          <li>
            <Trans>hide themselves from friends tables on the Friends page.</Trans>
          </li>
        </ClauseList>
        <Clause n="8.2">
          <Trans>
            Requests are sent to {email} with a link to the Steam profile. The Provider replies
            within 10 working days. If the User cannot sign in, the Provider deletes the account on
            such a request.
          </Trans>
        </Clause>
      </LegalSection>

      <LegalSection title={<Trans>9. Minors</Trans>}>
        <Clause n="9.1">
          <Trans>
            The Service is intended for people over 14. Users from the European Union may use it
            from the age at which they can consent to data processing in their country (13 to 16).
            Younger users need their parents' consent.
          </Trans>
        </Clause>
        <Clause n="9.2">
          <Trans>
            If a child has signed up without their parents knowing, the parents can write to {email}{" "}
            and the Provider will delete the account.
          </Trans>
        </Clause>
      </LegalSection>

      <LegalSection title={<Trans>10. Users from the European Union</Trans>}>
        <Clause n="10.1">
          <Trans>
            For users from the EU and EEA, the data in section 3 is personal data within the meaning
            of the General Data Protection Regulation (GDPR), and the Provider is its controller.
          </Trans>
        </Clause>
        <Clause n="10.2">
          <Trans>
            Legal bases: providing the Service — GDPR Art. 6(1)(b); payment records — Art. 6(1)(c);
            reliability, statistics and data about friends and other players of a match — the
            Provider's legitimate interest, Art. 6(1)(f).
          </Trans>
        </Clause>
        <Clause n="10.3">
          <Trans>
            In addition to the rights in section 8, a user from the EU may receive their data in a
            machine-readable format, restrict or object to processing and lodge a complaint with the
            data protection authority of their country. The Provider replies to such requests within
            one month.
          </Trans>
        </Clause>
      </LegalSection>

      <LegalSection title={<Trans>11. Changes to this policy</Trans>}>
        <Clause n="11.1">
          <Trans>
            A new version of the Policy is published on this page with the date at the top. The
            Provider announces significant changes on the site in advance.
          </Trans>
        </Clause>
      </LegalSection>

      <SellerCard title={<Trans>12. Provider details</Trans>} />
    </LegalDocument>
  );
};
